import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { Virtuoso } from "react-virtuoso";
import { motion, AnimatePresence } from "framer-motion";
import ChatInput from "./ChatInput";
import ChatMessageBubble from "../chat-bubble";
import ChatImageViewer from "../chat-session-info/ChatImageViewer";
import ChatPatientForm from "./ChatPatientForm";
import { DocumentSharingWindow } from "../anokye-content/components/anokye-sharing-window";
import ModelParamsControls from "../../components/model-params-controls";
import SampleTaskCards from "./SampleTaskCards";
import { SAMPLE_TASK_EVENT } from "./sampleTask";
import ToolsList from "./ToolsList";
import { getFileExtension } from "@minohealth/common/utils/files";
import useDialogOpenState from "@minohealth/common/hooks/useDialogOpenState";
import { useSelector } from "react-redux";
import { HEALTH_WORKER_ROLE_VALUE } from "@minohealth/common/components/user-categorisation/utils";
import { useChatAuthContext } from "../../components/layout/chat-layout";

import { IoWarningOutline, IoInformationCircleOutline, IoTimeOutline, IoWifiOutline, IoServerOutline, IoAlertCircleOutline, IoRefreshOutline } from "react-icons/io5";
import { ArrowDown } from "lucide-react";
import logo from "../../assets/images/logo.png";
import { getRateLimitData } from "@minohealth/common/services/LLMApiService";
import { ModeInfos } from "../../constants/modeInfos";
import { toolsToShow, useBioTools } from "../../hooks/useBioTools";

import "./chat-content.css";
import useEventListener from "@minohealth/common/hooks/useEventListener";
import { useChatContext, AgentMode, OPEN_REPORT_FORM_EVENT, DEEP_RESEARCH_USER } from "@/services/chat-provider";

// DEV ONLY — remove before shipping
import { CANVAS_DUMMY_MESSAGES } from "../canvas/__dev__/dummyMessages"

// Defined outside ChatContent so React never sees these as new component types
// on re-render (which would cause Virtuoso to remount / flash the list).
const VirtuosoMessageList = React.forwardRef(function VirtuosoMessageList({ style, children, ...props }, ref) {
  return (
    <ul ref={ref} style={style} className="diagnose-chat__chat-messages-list" {...props}>
      {children}
    </ul>
  )
})

function VirtuosoMessageItem({ children, ...props }) {
  return (
    <li {...props} style={{ ...props.style, listStyle: 'none' }}>
      {children}
    </li>
  )
}

const VIRTUOSO_COMPONENTS = { List: VirtuosoMessageList, Item: VirtuosoMessageItem }

export default function ChatContent({ startGeneratingReport, openReportPanel, openReportFormDialog, onToggleSidebar, onToggleGallery, isArtifactsOpen }) {
  const { useActiveChatSessionId, useActiveChatSession, useStreamingMessage, useEditChatMessage, useRegenerateChatMessage, useSendChatFeedback, useSendAgentMessage, useSendChatMessage, useCancelActiveChatStream, useIsGuestMode, useGuestMessages, useAgentMode, useActiveChatMessages } = useChatContext()

  const { user } = useSelector((state) => state.app.datastate);
  const { authenticate } = useChatAuthContext();

  const [mode] = useAgentMode()
  const isAgentMode = mode != AgentMode.DEFAULT
  const { data: fetchedBioTools } = useBioTools(mode === AgentMode.MOREMI_CO_RESEARCHER ? user : null);
  const bioTools = toolsToShow(fetchedBioTools, ModeInfos[AgentMode.MOREMI_CO_RESEARCHER]?.tools);
  const [activeChatSessionId, setActiveChatSessionId] = useActiveChatSessionId()
  const {
    data: activeChatSession,
    isLoading: isLoadingChatSession,
  } = useActiveChatSession()
  const streamingMessage = useStreamingMessage(activeChatSessionId)

  const { mutate: sendFeedback } = useSendChatFeedback()
  const { mutate: editMessage } = useEditChatMessage()
  const { mutate: regenerateMessage } = useRegenerateChatMessage()
  const { mutate: sendAgentMessage } = useSendAgentMessage()
  const { mutate: cancelStream } = useCancelActiveChatStream()

  // Determine if guest or authenticated
  const isGuest = useIsGuestMode();
  const guestMessages = useGuestMessages()
  const {
    data: historicalMessages,
    isLoading: isLoadingMessages,
    fetchPreviousPage,
    isFetchingPreviousPage,
    hasPreviousPage,
  } = useActiveChatMessages()
  // DEV ONLY — remove before shipping. Visit with ?canvasDemo=1 to preview the Canvas
  // feature against fixture data without a backend.
  const isCanvasDemo = import.meta.env.DEV && new URLSearchParams(window.location.search).get("canvasDemo") === "1"
  const chatMessages = isCanvasDemo ? CANVAS_DUMMY_MESSAGES : (isGuest ? guestMessages : (historicalMessages ?? []));
  const isLoadingContent = !isCanvasDemo && (isLoadingChatSession || (!isGuest && activeChatSessionId != null && isLoadingMessages))
  const shouldShowChatMessages = isCanvasDemo || chatMessages.length > 0 || streamingMessage.query != null;

  useEventListener(document, OPEN_REPORT_FORM_EVENT, event => {
    const chatSessionId = event.detail.chatSessionId
    if (chatSessionId !== activeChatSession?._id) return

    openReportFormForAgentResponse()
  })

  function openReportFormForAgentResponse() {
    openReportFormDialog(null, {
      onSubmit: (data) => {
        const query = [
          // `Patient name: ${data.patientInfo.name}`,
          // `Patient ID: ${data.patientInfo.id}`,
          `Patient Age: ${data.patientInfo.age} years`,
          `Patient Gender: ${data.patientInfo.gender}`,

          `Modality: ${data.modality}`,
          `Techique: ${data.technique}`,

          data.examinationReason.trim() != "" && `Examination Reason: ${data.examinationReason}`,
          data.clinicalHistory.trim() != "" && `Clinical History: ${data.clinicalHistory}`,
        ].filter(Boolean).join("\n")

        sendAgentMessage({
          sessionId: activeChatSessionId,
          query,
          chatSessionId: activeChatSession?._id,
          mode: AgentMode.AGENT,
          forceAgentMode: AgentMode.AGENT,
        })
      }
    })
  }


  const chatContentRef = useRef(null)
  const virtuosoRef = useRef(null)
  // State mirror of chatContentRef so Virtuoso's customScrollParent re-renders
  // once the element is available (refs are null on first render).
  const [chatContentEl, setChatContentEl] = useState(null)
  const setChatContentRef = useCallback((el) => {
    chatContentRef.current = el
    setChatContentEl(el)
  }, [])

  const streamingMessageCount = (streamingMessage?.query ? 1 : 0) + (streamingMessage?.response ? 1 : 0);
  const isStreaming = streamingMessageCount > 0;
  const totalMessageCount = chatMessages.length + streamingMessageCount;
  const isDeepResearchStreaming = isStreaming && streamingMessage.response?.user === DEEP_RESEARCH_USER;

  const prevHasQueryRef = useRef(false);
  const prevHasResponseRef = useRef(false);

  const [scrollBtn, setScrollBtn] = useState({ atBottom: true });
  const scrollBtnRef = useRef({ atBottom: true });
  const prevSessionIdRef = useRef(null);

  const scrollToBottom = useCallback((smooth = false) => {
    virtuosoRef.current?.scrollToIndex({
      index: 'LAST',
      behavior: smooth ? 'smooth' : 'auto',
      align: 'end',
    })
  }, [])

  const scrollToTop = () => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = 0;
    }
  };

  // Virtuoso reverse-infinite-scroll: firstItemIndex only decreases when older messages
  // are *prepended* via fetchPreviousPage — NOT on every append (stream commit).
  // Treating appends as prepends (the old computed approach) would fight scroll position.
  const ITEM_COUNT_ESTIMATE = 100000
  const [firstItemIndex, setFirstItemIndex] = useState(ITEM_COUNT_ESTIMATE)
  const countBeforeFetchRef = useRef(null)

  // Reset when switching sessions
  useEffect(() => {
    setFirstItemIndex(ITEM_COUNT_ESTIMATE)
  }, [activeChatSessionId])

  // Capture count when fetch starts; apply delta when fetch completes
  useEffect(() => {
    if (isFetchingPreviousPage) {
      if (countBeforeFetchRef.current == null) {
        countBeforeFetchRef.current = chatMessages.length
      }
    } else if (countBeforeFetchRef.current != null) {
      const prepended = chatMessages.length - countBeforeFetchRef.current
      if (prepended > 0) setFirstItemIndex(prev => Math.max(0, prev - prepended))
      countBeforeFetchRef.current = null
    }
  }, [isFetchingPreviousPage, chatMessages.length])

  const startReached = useCallback(() => {
    if (hasPreviousPage && !isFetchingPreviousPage) {
      fetchPreviousPage()
    }
  }, [hasPreviousPage, isFetchingPreviousPage, fetchPreviousPage])

  // Combined historical + in-flight streaming messages for Virtuoso's data array.
  // Historical items receive the stable `chatMessages` reference as their `messages`
  // prop so React.memo can bail out during streaming; streaming items get allMessages.
  const allMessages = useMemo(() => {
    const streaming = [streamingMessage.query, streamingMessage.response].filter(Boolean)
    return [...chatMessages, ...streaming]
  }, [chatMessages, streamingMessage.query, streamingMessage.response])

  
  // Only scroll to bottom when genuinely switching to a different session,
  // not on the null-bounce caused by refetch (ID → null → same ID).
  useEffect(() => {
    if (!activeChatSessionId || isLoadingContent) return;
    if (prevSessionIdRef.current === activeChatSessionId) return;
    prevSessionIdRef.current = activeChatSessionId;
    scrollToBottom();
  }, [activeChatSessionId, isLoadingContent]);

  
  useEffect(() => {
    const hasQuery = !!streamingMessage?.query;
    const hasResponse = !!streamingMessage?.response;

    const queryJustStarted = !prevHasQueryRef.current && hasQuery;

    prevHasQueryRef.current = hasQuery;
    prevHasResponseRef.current = hasResponse;

    if (queryJustStarted) {
      virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'start', behavior: 'auto' })
    }
  }, [streamingMessage?.query, streamingMessage?.response]);

  // atBottom is now tracked via Virtuoso's atBottomStateChange callback.



  useEffect(() => {
    if (!shouldShowChatMessages) {
      window.scrollTo(0, 0);

      const chatContent = chatContentRef.current;
      if (chatContent) {
        chatContent.scrollTop = 0;
      }

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  }, [shouldShowChatMessages]);

  // * Preview image stuff
  const previewImageDialogRef = useRef(null);
  const [, setShouldImageDialogOpen] = useDialogOpenState(previewImageDialogRef);
  const [previewImageId, setPreviewImageId] = useState("");
  const [previewImageBboxes, setPreviewImageBboxes] = useState([]);

  function previewImage(id, bboxes) {
    setPreviewImageId(id);
    if (bboxes != null) setPreviewImageBboxes(bboxes);
    setShouldImageDialogOpen(true);
  }

  function stopImagePreview() {
    setShouldImageDialogOpen(false);
    setPreviewImageId(null);
    setPreviewImageBboxes([]);
  }

  // * Patient info stuff
  const patientFormDialogRef = useRef(null);
  const [, setIsPatientDialogOpen] = useDialogOpenState(patientFormDialogRef);

  const documentSharingWindowRef = useRef(null);

  function shareDocument(document) {
    documentSharingWindowRef.current.openWindow(document);
  }

  if (activeChatSessionId != null && !isLoadingChatSession && activeChatSession == null) {
    setActiveChatSessionId(null);
  }

  return (
    <div className="diagnose-chat__content" ref={setChatContentRef} data-deep-research={isDeepResearchStreaming}>
      {/* Show patient info prompt only for authenticated users */}
      {activeChatSession != null &&
        activeChatSession?.patientInfo == null &&
        user.role === HEALTH_WORKER_ROLE_VALUE &&
        mode !== AgentMode.MOREMI_CO_RESEARCHER && (
          <div className="diagnose-chat__patient-info-flash">
            <IoInformationCircleOutline />
            <span>Add patient information for easy access of history</span>
            <button
              className="diagnose-chat__patient-info-flash-btn"
              onClick={() => setIsPatientDialogOpen(true)}>
              Add
            </button>

            <dialog
              className="chat-patient-form-dialog"
              ref={patientFormDialogRef}
              onCancel={(ev) => ev.preventDefault()}>
              <ChatPatientForm
                onClose={() => {
                  setIsPatientDialogOpen(false);
                }}
              />
            </dialog>
          </div>
        )}

      {(!shouldShowChatMessages && !isLoadingContent) && (
        <div className="diagnose-chat__welcome-content">
          <div className="welcome-hero">
            <div className="welcome-hero__logo">
              <img src={logo} alt="minoHealth" width={40} height={40} />
              <div className="welcome-hero__text">
                <AnimatePresence mode="wait">
                  <motion.h1
                    key={mode}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    {ModeInfos[mode]?.title || "Moremi"}
                  </motion.h1>
                </AnimatePresence>
                {/* <span className="welcome-hero__badge">v2.0</span> */}
              </div>
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={mode}
                className="welcome-hero__subtitle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {ModeInfos[mode]?.description || "Your AI-powered medical assistant for diagnosis, treatment, and medical insights"}
              </motion.p>
            </AnimatePresence>

            {/* Tools List - shows when mode has tools */}
            {mode === AgentMode.MOREMI_CO_RESEARCHER && bioTools && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="mt-6 w-full max-w-2xl"
              >
                <ToolsList tools={bioTools} />
              </motion.div>
            )}
          </div>
        </div>
      )}

      {isDeepResearchStreaming && <div className="deep-research-gradient-bar" aria-hidden="true" />}

      {(isLoadingContent || shouldShowChatMessages) && (
        <div className="diagnose-chat__chat-messages">
          {isLoadingContent && (
            <ul className="diagnose-chat__chat-messages-list">
              <li>
                <div className="chat-bubble w-auto! max-w-[50%]!" data-variant="user">
                  <p className="skeleton"></p>
                  <p className="skeleton"></p>
                </div>
              </li>
              <li>
                <div className="chat-bubble" data-variant="ai">
                  <p className="skeleton"></p>
                  <p className="skeleton max-w-[95%]"></p>
                  <p className="skeleton max-w-[80%]"></p>
                </div>
              </li>
            </ul>
          )}

          {isFetchingPreviousPage && (
            <div className="flex justify-center py-3 text-sm text-muted-foreground">
              <span className="skeleton w-32 h-4 rounded" />
            </div>
          )}

          {shouldShowChatMessages && !isLoadingContent && chatContentEl && (
            <Virtuoso
              ref={virtuosoRef}
              firstItemIndex={firstItemIndex}
              startReached={startReached}
              customScrollParent={chatContentEl}
              data={allMessages}
              computeItemKey={(index, message) => message._id ?? `streaming-${index}`}
              components={VIRTUOSO_COMPONENTS}
              overscan={600}
              atBottomStateChange={(atBottom) => {
                const next = { ...scrollBtnRef.current, atBottom }
                scrollBtnRef.current = next
                setScrollBtn(next)
              }}
              itemContent={(index, message) => {
                const localIndex = index - firstItemIndex
                const isStreamingMsg = localIndex >= chatMessages.length
                const totalLength = allMessages.length
                // Pass the stable chatMessages ref to historical items so
                // React.memo can bail out during streaming; streaming items
                // need allMessages for full context (e.g. image lookup).
                const messagesProp = isStreamingMsg ? allMessages : chatMessages
                return (
                  <div
                    id={`chat-message-${localIndex}`}
                    style={{
                      maxWidth: '90ch',
                      marginInline: 'auto',
                      paddingBottom: '2rem',
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <ChatMessageBubble
                      message={message}
                      messages={messagesProp}
                      sendFeedback={isGuest || !sendFeedback ? undefined : (data) => sendFeedback({ chatIndex: localIndex, data })}
                      previewImage={previewImage}
                      previewReport={openReportPanel}
                      openReportFormForAgentResponse={openReportFormForAgentResponse}
                      shareDocument={shareDocument}
                      regenerateMessage={isGuest || !regenerateMessage ? undefined : (() => regenerateMessage({ messageId: message._id }))}
                      editMessage={isGuest || !editMessage ? undefined : ((text) => {
                        cancelStream()
                        editMessage({ messageId: message._id, text })
                      })}
                      messageIndex={localIndex}
                      messagesLength={totalLength}
                      isLatestMessage={localIndex === totalLength - 1}
                      isStreaming={isStreamingMsg}
                    />
                  </div>
                )
              }}
            />
          )}
          <DocumentSharingWindow ref={documentSharingWindowRef} />
        </div>
      )}

      <ChatImageViewer
        ref={previewImageDialogRef}
        srcImageId={previewImageId}
        bboxes={previewImageBboxes}
        // multiple={hasMultipleImages}
        onCloseViewer={stopImagePreview}
      />

      <div className="diagnose-chat__content-footer bg-background">
        <AnimatePresence>
          {shouldShowChatMessages && !scrollBtn.atBottom && (
            <motion.button
              key="scroll-nav"
              className="scroll-nav-btn"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              onClick={() => scrollToBottom(true)}
              aria-label="Scroll to bottom"
            >
              <ArrowDown size={17} strokeWidth={1.5} />
            </motion.button>
          )}
        </AnimatePresence>
        <div></div>
        <ChatInput
          startGeneratingReport={startGeneratingReport}
          showDisclaimer={shouldShowChatMessages}
          onToggleSidebar={onToggleSidebar}
          onToggleGallery={onToggleGallery}
          isArtifactsOpen={isArtifactsOpen}
          isDeepResearchStreaming={isDeepResearchStreaming}
        />
      </div>

      {/* Sample Task Cards - Only show when no messages */}
      {(!shouldShowChatMessages && !isLoadingContent) && (
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.05 }}
            className="w-full flex justify-center px-4 pb-8"
          >
            <SampleTaskCards
              mode={mode}
              onTaskClick={(task) => {
                document.dispatchEvent(new CustomEvent(SAMPLE_TASK_EVENT, { detail: { text: task.text } }));
              }}
            />
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

// Enhanced Error Bubble Component
function EnhancedErrorBubble({ error, getErrorHandler, isGuest, authenticate }) {
  // Safety checks for error object
  if (!error) {
    return (
      <div
        data-test="diagnose-chat-error-bubble"
        className="modern-error-bubble"
        style={{
          maxWidth: '600px',
          margin: '1rem auto',
          padding: '1rem',
          background: 'var(--error-bg-gradient)',
          border: '1px solid var(--destructive-border)',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-lg)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{
            color: 'var(--destructive)',
            fontSize: '1.25rem',
            padding: '0.5rem',
            background: 'var(--destructive-bg)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <IoAlertCircleOutline />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: 'var(--foreground)',
              margin: '0 0 0.25rem 0'
            }}>
              Unknown Error
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--muted-foreground)',
              margin: 0,
              lineHeight: 1.5
            }}>
              An unexpected error occurred. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Handle different error object structures
  const errorMessage = error?.message || error?.error || error?.description || error?.text || JSON.stringify(error) || '';
  const errorStatus = error?.status || error?.statusCode || error?.code || 0;

  // Enhanced rate limit error detection
  const isRateLimitError = errorMessage.toLowerCase().includes('rate limit') ||
    errorMessage.toLowerCase().includes('too many requests') ||
    errorMessage.toLowerCase().includes('maximum of') ||
    errorMessage.toLowerCase().includes('try again in') ||
    errorMessage.toLowerCase().includes('requests per') ||
    errorStatus === 429;

  if (isRateLimitError) {
    return (
      <RateLimitErrorDisplay
        errorMessage={errorMessage}
        isGuest={isGuest}
        authenticate={authenticate}
      />
    );
  }

  const isNetworkError = errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('connection') ||
    errorMessage.toLowerCase().includes('fetch');

  const isServerError = errorStatus >= 500;

  const getErrorConfig = () => {
    if (isNetworkError) {
      return {
        icon: IoWifiOutline,
        title: 'Network Connection Problem',
        bgGradient: 'linear-gradient(135deg, rgba(224, 36, 36, 0.08), rgba(224, 36, 36, 0.12))',
        borderColor: 'rgba(224, 36, 36, 0.25)',
        iconBg: 'rgba(224, 36, 36, 0.15)',
        iconColor: '#e02424',
        accentColor: '#e02424',
      };
    }

    if (isServerError) {
      return {
        icon: IoServerOutline,
        title: 'Server Error',
        bgGradient: 'linear-gradient(135deg, rgba(220, 38, 38, 0.08), rgba(220, 38, 38, 0.12))',
        borderColor: 'rgba(220, 38, 38, 0.25)',
        iconBg: 'rgba(220, 38, 38, 0.15)',
        iconColor: '#dc2626',
        accentColor: '#dc2626',
      };
    }

    return {
      icon: IoAlertCircleOutline,
      title: 'Error',
      bgGradient: 'linear-gradient(135deg, rgba(224, 36, 36, 0.08), rgba(224, 36, 36, 0.12))',
      borderColor: 'rgba(224, 36, 36, 0.25)',
      iconBg: 'rgba(224, 36, 36, 0.15)',
      iconColor: '#e02424',
      accentColor: '#e02424',
    };
  };

  const getDetailedErrorMessage = () => {
    if (isNetworkError) {
      return (
        <>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: '0 0 0.75rem 0',
            lineHeight: 1.6
          }}>
            Unable to connect to the server. This could be due to:
          </p>
          <ul style={{
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.7)',
            margin: '0 0 0.75rem 0',
            paddingLeft: '1.25rem',
            lineHeight: 1.6
          }}>
            <li>Your internet connection is offline</li>
            <li>The server is temporarily unreachable</li>
            <li>A firewall is blocking the connection</li>
          </ul>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            padding: '0.75rem',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            marginTop: '0.75rem'
          }}>
            <IoInformationCircleOutline style={{
              fontSize: '1rem',
              color: '#60a5fa',
              marginTop: '0.125rem',
              flexShrink: 0
            }} />
            <p style={{
              fontSize: '0.8125rem',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: 0,
              lineHeight: 1.5
            }}>
              <strong>Troubleshooting:</strong> Check your internet connection, refresh the page, or try again in a few minutes.
            </p>
          </div>
        </>
      );
    }

    if (isServerError) {
      return (
        <>
          <p style={{
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.8)',
            margin: '0 0 0.75rem 0',
            lineHeight: 1.6
          }}>
            Our server encountered an error while processing your request. This is not your fault.
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            padding: '0.75rem',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            marginTop: '0.75rem'
          }}>
            <IoInformationCircleOutline style={{
              fontSize: '1rem',
              color: '#60a5fa',
              marginTop: '0.125rem',
              flexShrink: 0
            }} />
            <div>
              <p style={{
                fontSize: '0.8125rem',
                color: 'rgba(255, 255, 255, 0.9)',
                margin: '0 0 0.5rem 0',
                lineHeight: 1.5,
                fontWeight: 600
              }}>
                What you can do:
              </p>
              <ul style={{
                fontSize: '0.8125rem',
                color: 'rgba(255, 255, 255, 0.7)',
                margin: 0,
                paddingLeft: '1.25rem',
                lineHeight: 1.5
              }}>
                <li>Wait a few moments and try again</li>
                <li>Refresh the page and retry your request</li>
                <li>Contact support if the problem persists</li>
              </ul>
            </div>
          </div>
        </>
      );
    }

    // Default error message
    const defaultErrorMessage = error?.message || error?.error || error?.statusText || 'An unexpected error occurred';

    return (
      <>
        <p style={{
          fontSize: '0.875rem',
          color: 'rgba(255, 255, 255, 0.8)',
          margin: '0 0 0.75rem 0',
          lineHeight: 1.6
        }}>
          {defaultErrorMessage}
        </p>
        {errorStatus > 0 && (
          <div style={{
            display: 'inline-block',
            padding: '0.375rem 0.75rem',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '6px',
            marginTop: '0.5rem'
          }}>
            <p style={{
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: 0,
              fontFamily: 'monospace',
              fontWeight: 500
            }}>
              Error Code: {errorStatus}
            </p>
          </div>
        )}
      </>
    );
  };

  const config = getErrorConfig();
  const ErrorIcon = config.icon;

  return (
    <div
      data-test="diagnose-chat-error-bubble"
      className="modern-error-bubble"
      style={{
        maxWidth: '600px',
        margin: '1rem auto',
        padding: '1.25rem',
        background: config.bgGradient,
        border: `1px solid ${config.borderColor}`,
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{
          color: config.iconColor,
          fontSize: '1.5rem',
          padding: '0.625rem',
          background: config.iconBg,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <ErrorIcon />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: '#ffffff',
            margin: '0 0 0.5rem 0',
            letterSpacing: '-0.01em'
          }}>
            {config.title}
            {isServerError && errorStatus > 0 && (
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.6)',
                marginLeft: '0.5rem'
              }}>
                ({errorStatus})
              </span>
            )}
          </h3>

          {getDetailedErrorMessage()}

          {error.action != null && (
            <button
              type="button"
              onClick={getErrorHandler(error.action)}
              style={{
                marginTop: '1rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#ffffff',
                background: config.accentColor,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                e.currentTarget.style.opacity = '1';
              }}
            >
              <IoRefreshOutline style={{ fontSize: '1rem' }} />
              {error.actionTitle || 'Retry'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Rate Limit Error Component
function RateLimitErrorDisplay({ errorMessage, isGuest, authenticate }) {
  const [timeLeft, setTimeLeft] = useState('');

  const parseRateLimitMessage = (message) => {
    const maxRequestsMatch = message.match(/maximum of (\d+) requests per (\d+) (hours?|minutes?|days?)/i);
    const tryAgainMatch = message.match(/try again in (\d+) (hours?|minutes?|days?)/i);

    let maxRequests = maxRequestsMatch ? parseInt(maxRequestsMatch[1]) : 5;
    let waitTime = null;
    let waitUnit = null;

    if (tryAgainMatch) {
      waitTime = parseInt(tryAgainMatch[1]);
      waitUnit = tryAgainMatch[2];
    }

    return { maxRequests, waitTime, waitUnit };
  };

  useEffect(() => {
    const rateLimitData = getRateLimitData();
    if (!rateLimitData) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeUntilReset = Math.max(0, rateLimitData.resetTime - now);

      if (timeUntilReset === 0) {
        setTimeLeft("Ready to retry");
        clearInterval(interval);
        return;
      }

      const hours = Math.floor(timeUntilReset / (1000 * 60 * 60));
      const minutes = Math.floor((timeUntilReset % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeUntilReset % (1000 * 60)) / 1000);

      let timeDisplay = '';
      if (hours > 0) timeDisplay += `${hours}h `;
      if (minutes > 0) timeDisplay += `${minutes}m `;
      if (hours === 0) timeDisplay += `${seconds}s`;

      setTimeLeft(timeDisplay.trim());
    }, 1000);

    return () => clearInterval(interval);
  }, [errorMessage]);

  const { maxRequests, waitTime, waitUnit } = parseRateLimitMessage(errorMessage);
  const waitTimeText = waitTime && waitUnit ? `${waitTime} ${waitUnit}` : 'a while';
  const timeToReset = timeLeft || `in ${waitTimeText}`;

  return (
    <div className="rate-limit-error">
      <div className="rate-limit-error__icon">
        <IoTimeOutline />
      </div>
      <div className="rate-limit-error__content">
        <p className="rate-limit-error__title">Rate Limit Exceeded</p>
        <p className="rate-limit-error__message">
          You've reached your request limit of {maxRequests}. Please try again later. Next request available {timeLeft ? 'in ' : ''}<span className="rate-limit-error__time">{timeToReset}</span>
        </p>
      </div>
      {isGuest && (
        <div className="rate-limit-error__action">
          <button className="rate-limit-error__login-btn" onClick={() => authenticate().catch(console.error)}>
            Login for More Access
          </button>
          <p className="rate-limit-error__login-perks">Get unlimited requests and premium features.</p>
        </div>
      )}
    </div>
  );
}

// Utility functions for file type checking (currently unused but kept for future use)
// eslint-disable-next-line no-unused-vars
function isDicomFile(fileName) {
  const fileExtension = getFileExtension(fileName);
  return ["dcm", "dicom"].includes(fileExtension);
}

// eslint-disable-next-line no-unused-vars
function isNiftiFile(fileName) {
  const fileExtension = getFileExtension(fileName);
  return ["nii", "gz"].includes(fileExtension);
}
